import * as React from 'react';
import {
    EnvironmentFilled,
    ArrowRightOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
} from '@ant-design/icons';
import { Divider, Tooltip } from 'antd';
import moment from 'moment';
import { StylesProps } from '../../../../../theme/jss-types';
import { gridView1Styles } from './grid-view-1.styles';
import { getDashboardStatusGroup } from '../utils';
import { getCarrierLogo } from '../constants';
import StatusPrettyName from 'components/consignments/status-pretty-name';
import deliveryTruckSpeedIcon from '../../../../../assets/delivery_truck_speed.svg';
import hourglassTopIcon from '../../../../../assets/hourglass_top.svg';
import { useTranslation } from 'react-i18next';

interface GridView1CardProps extends StylesProps<ReturnType<typeof gridView1Styles>> {
    consignment: any;
    onClick: (consignment: any) => void;
}

const GridView1Card: React.FC<GridView1CardProps> = (props) => {
    const { consignment, onClick, classes } = props;
    const { t } = useTranslation();

    // Get grouped status (returns 'All' if no match)
    const statusGroup = getDashboardStatusGroup(consignment.status);
    const hasStatusGroup = statusGroup && statusGroup !== 'All';

    const carrierLogoUrl = getCarrierLogo(consignment.courier_partner);

    const getStatusBadgeClass = () => {
        const baseClasses = [classes.statusBadge];
        switch (statusGroup) {
            case 'In Process':
                baseClasses.push(classes.statusInProcess);
                break;
            case 'In Transit':
                baseClasses.push(classes.statusInTransit);
                break;
            case 'Delivered':
                baseClasses.push(classes.statusDelivered);
                break;
            case 'Cancelled':
                baseClasses.push(classes.statusCancelled);
                break;
            default:
                // For unmatched statuses, use In Process styling as default
                baseClasses.push(classes.statusInProcess);
        }
        return baseClasses.join(' ');
    };

    const getStatusIcon = () => {
        switch (statusGroup) {
            case 'In Process':
                return <img src={hourglassTopIcon} alt="" style={{ width: 14, height: 14 }} />;
            case 'In Transit':
                return <img src={deliveryTruckSpeedIcon} alt="" style={{ width: 14, height: 14 }} />;
            case 'Delivered':
                return <CheckCircleOutlined />;
            case 'Cancelled':
                return <CloseCircleOutlined />;
            default:
                return <img src={hourglassTopIcon} alt="" style={{ width: 14, height: 14 }} />;
        }
    };

    const handleCustomerRefClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onClick(consignment);
    };

    const formatDate = (dateValue: any) => {
        if (!dateValue) return '-';
        return moment(dateValue).format('DD MMM, YYYY');
    };

    const formatLocation = (name: string, country: string) => {
        if (!name && !country) return '-';
        if (!country) return name;
        if (!name) return country;
        return `${name}, ${country}`;
    };

    return (
        <div className={classes.card}>
            <div className={classes.cardHeader}>
                <div className={classes.avatar}>
                    {carrierLogoUrl ? (
                        <img
                            src={carrierLogoUrl}
                            alt={consignment.carrier_code || 'Carrier'}
                            className={classes.carrierLogoImage}
                        />
                    ) : null}
                </div>

                <div className={classes.headerContent}>
                    <div className={classes.shipmentId} onClick={handleCustomerRefClick}>
                        {consignment.customer_reference_number ?? '-'}
                    </div>

                    <div className={getStatusBadgeClass()}>
                        {getStatusIcon()}
                        {hasStatusGroup
                            ? t(statusGroup)
                            : <StatusPrettyName status={consignment.status} useMariaDb />}
                    </div>
                </div>
            </div>

            <div className={classes.detailsSection}>
                <Divider style={{ margin: 0 }} />

                <div className={classes.detailsRow}>
                    <div className={classes.detailItem}>
                        <div className={classes.detailLabel}>{t('protocol_id')}</div>
                        <Tooltip title={consignment.customer_code}>
                            <div className={classes.detailValue}>
                                {consignment.customer_code || <span className={classes.emptyValue}>-</span>}
                            </div>
                        </Tooltip>
                    </div>

                    <div className={classes.detailItem}>
                        <div className={classes.detailLabel}>{t('awb')}</div>
                        <Tooltip title={consignment.courier_partner_reference_number}>
                            <div className={classes.detailValue}>
                                {consignment.courier_partner_reference_number
                                    || <span className={classes.emptyValue}>-</span>}
                            </div>
                        </Tooltip>
                    </div>
                </div>

                <Divider style={{ margin: 0 }} />

                <div className={classes.locationSection}>
                    <div className={classes.locationItem}>
                        <div className={classes.locationHeader}>
                            <EnvironmentFilled />
                            <span className={classes.detailLabel}>{t('origin')}</span>
                        </div>
                        <div className={classes.locationText}>
                            {formatLocation(consignment.sender_name, consignment.sender_country)}
                        </div>
                    </div>

                    <ArrowRightOutlined className={classes.locationArrow} />

                    <div className={classes.locationItemRight}>
                        <div className={classes.locationHeader}>
                            <EnvironmentFilled />
                            <span className={classes.detailLabel}>{t('destination')}</span>
                        </div>
                        <div className={classes.locationTextRight}>
                            {formatLocation(consignment.destination_name, consignment.destination_country)}
                        </div>
                    </div>
                </div>

                <div className={classes.datesSection}>
                    <div className={classes.detailItem}>
                        <div className={classes.detailLabel}>{t('pickup_date')}</div>
                        <div className={classes.detailValue}>
                            {consignment.pickup_time
                                ? formatDate(consignment.pickup_time)
                                : <span className={classes.emptyValue}>-</span>}
                        </div>
                    </div>

                    <div className={classes.detailItemRight}>
                        <div className={classes.detailLabel}>{t('created_date')}</div>
                        <div className={classes.detailValueRight}>
                            {formatDate(consignment.created_at)}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default React.memo(GridView1Card);
